import { type Health, normalizeAccessGatewayUrl } from '@xphoto/shared';

/**
 * Passerelle d'authentification (ADR-0008) — côté SPA.
 *
 * PROBLÈME : les membres d'une app tierce (pnyx) se partagent des URL PROFONDES de
 * xphoto (« regarde cette photo »), copiées-collées depuis la barre d'adresse. Le
 * destinataire qui n'a jamais présenté le lien d'accès `?access=<jeton>` n'a aucune
 * session → écran de connexion → cul-de-sac (il n'a ni mot de passe, ni le lien).
 *
 * PRINCIPE : xphoto ne sait pas qui est le visiteur, mais pnyx, LUI, le sait (c'est
 * de là qu'il vient). Un visiteur sans session est donc renvoyé à pnyx avec la page
 * qu'il visait (`?next=<chemin>`) ; pnyx, derrière SA garde d'authentification, le
 * renvoie ici avec le `?access=<jeton>` (gardé dans SA config serveur), que le boot
 * échange déjà (cf. `AuthProvider`). Le jeton ne transite donc JAMAIS dans les URL
 * que les gens se collent dans les fils.
 *
 * Ce module ne contient que des fonctions PURES (+ un marqueur de session) : la
 * navigation elle-même reste dans le composant.
 */

/**
 * Marqueur ANTI-BOUCLE. Sans lui : jeton révoqué → xphoto renvoie à pnyx → pnyx
 * renvoie le jeton → l'échange échoue → xphoto renvoie à pnyx → boucle infinie.
 * Un aller-retour est donc tenté UNE SEULE FOIS par onglet ; ensuite, écran de
 * connexion. `sessionStorage` (pas `localStorage`) : la portée est l'onglet, et un
 * nouvel onglet a droit à sa tentative.
 */
const ATTEMPT_KEY = 'xphoto.gatewayAttempt';

/** Tolérant à un `sessionStorage` indisponible (Safari privé, iframe cloisonnée…). */
export function markGatewayAttempt(): void {
  try {
    window.sessionStorage.setItem(ATTEMPT_KEY, '1');
  } catch {
    // Stockage indisponible : on perd l'anti-boucle, jamais la navigation.
  }
}

export function hasGatewayAttempt(): boolean {
  try {
    return window.sessionStorage.getItem(ATTEMPT_KEY) === '1';
  } catch {
    return false;
  }
}

/** Appelé dès qu'une session est ouverte : le prochain besoin aura droit à sa tentative. */
export function clearGatewayAttempt(): void {
  try {
    window.sessionStorage.removeItem(ATTEMPT_KEY);
  } catch {
    // Idem : sans stockage, rien à nettoyer.
  }
}

/**
 * Page visée, telle qu'on la redemandera à pnyx (`next`) : chemin RELATIF, jamais
 * une URL absolue (pnyx REFUSE tout ce qui ne commence pas par `/` — sans quoi il
 * enverrait le jeton chez un tiers). `access` est retiré : au boot, le routeur peut
 * encore voir l'URL d'origine (le nettoyage par `history.replaceState` a lieu dans
 * un effet), et un jeton mort ne doit pas repartir dans le `next`.
 */
export function gatewayTarget(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  params.delete('access');
  const query = params.toString();
  return `${pathname}${query.length > 0 ? `?${query}` : ''}`;
}

/**
 * `<passerelle>?next=<cible>`. `null` si l'URL configurée est inexploitable.
 *
 * DÉFENSE EN PROFONDEUR : l'URL est revalidée ici (`normalizeAccessGatewayUrl`,
 * http/https uniquement) alors que l'API la valide déjà à l'écriture — parce qu'elle
 * finit dans un `window.location.assign()` : une valeur `javascript:` arrivée par une
 * base trafiquée serait une injection. Le coût est nul, la garantie est locale.
 */
export function buildGatewayUrl(gatewayUrl: string, target: string): string | null {
  const normalized = normalizeAccessGatewayUrl(gatewayUrl);
  if (!normalized.ok || normalized.value === null) return null;
  try {
    const url = new URL(normalized.value);
    url.searchParams.set('next', target);
    return url.toString();
  } catch {
    return null;
  }
}

export type GatewayDecision =
  | { kind: 'wait' } // `/api/health` pas encore arrivé : ni rebond, ni login (pas de flash)
  | { kind: 'redirect'; url: string }
  | { kind: 'login' };

/**
 * Que faire d'un visiteur SANS session ? Fonction pure (tout l'état vient des
 * paramètres) — c'est ici que vit la logique, le composant n'est qu'une coquille.
 *
 * INVARIANT : en cas de doute (health injoignable, URL invalide, tentative déjà
 * faite, aucune passerelle), on retombe TOUJOURS sur l'écran de connexion. Une
 * passerelle en panne ne doit jamais rendre xphoto inaccessible à un admin.
 */
export function decideGateway(input: {
  health: Health | null;
  healthError: boolean;
  target: string;
  attempted: boolean;
}): GatewayDecision {
  const { health, healthError, target, attempted } = input;
  if (healthError) return { kind: 'login' };
  if (health === null) return { kind: 'wait' };
  if (health.accessGatewayUrl === null || attempted) return { kind: 'login' };
  const url = buildGatewayUrl(health.accessGatewayUrl, target);
  return url === null ? { kind: 'login' } : { kind: 'redirect', url };
}
